import { useEffect, useMemo, useRef } from 'react';

import { GENERATED_SKILL_DEFAULTS_VERSION } from '@revelio/data-access/skill-defaults';

import { kibanaLogger } from '../../../../shared/logging/kibana-logger';
import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { SKILL_TAXONOMY_SELECTION_LIST_KEYS } from '../../../../shared/selection-lists/types';
import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { deriveEffectiveTaxonomiesForScope } from '../../../../shared/user-preferences/utils/derive-effective-taxonomies';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import { reconcileVersionedDefaults } from '../../../../shared/user-preferences/utils/reconcileVersionedDefaults';
import { useToast } from '../../hooks/useToast';
import type { TreeFilter } from '../types/filters';

/** How many skills to seed when falling back to the live tree. */
const DEFAULT_SKILL_COUNT = 5;

/**
 * Surfaces the skill default-filter version reconciliation as a side effect
 * and, when both the saved preference and the baked-in defaults are stale,
 * supplies the first N top-level skills from the live taxonomy tree as a
 * fallback selection.
 *
 * - saved stale (baked current) → info toast ("no longer apply"), caller uses coded defaults.
 * - saved + baked stale → error toast + Kibana log, caller uses `firstFromLiveSelections`.
 *
 * Toasts/logs fire from an effect (never during the synchronous default-map build).
 */
export const useSkillDefaultsStaleNotice = ({
  enabled,
  currentSkillVersion,
}: {
  enabled: boolean;
  currentSkillVersion?: string;
}): { firstFromLiveSelections?: TreeFilter['selections'] } => {
  const toast = useToast();
  const notifiedRef = useRef<string | null>(null);

  const skillPrefs = PreferenceBrowserStorage.get(PreferenceScope.SKILL);
  const savedSelections = skillPrefs?.[UserPreferenceKey.DefaultFilters];
  // Taxonomy choice is GLOBAL (derived: adaptive → generic on skill pages).
  const globalTaxonomies = deriveEffectiveTaxonomiesForScope(
    PreferenceBrowserStorage.get(PreferenceScope.GLOBAL)?.[
      UserPreferenceKey.Taxonomies
    ],
    PreferenceScope.SKILL
  );
  const savedVersion = globalTaxonomies?.skillTaxonomyVersion;

  const result = reconcileVersionedDefaults({
    hasSaved: !!savedSelections && savedSelections.length > 0,
    savedVersion,
    currentVersion: currentSkillVersion,
    bakedVersion: GENERATED_SKILL_DEFAULTS_VERSION,
  });

  // Only read the live tree when we actually need the fallback. The tree hook
  // has no pause; when not needed the derived selections are simply ignored.
  const needLive = enabled && result.generatedStale;
  const skillTaxonomyId =
    typeof globalTaxonomies?.skill === 'number' ? globalTaxonomies.skill : 0;
  const { customSkillTree } = useCustomSkillTaxonomyTree(
    skillTaxonomyId,
    currentSkillVersion
  );

  const firstFromLiveSelections = useMemo<
    TreeFilter['selections'] | undefined
  >(() => {
    if (!needLive || !customSkillTree || customSkillTree.length === 0) {
      return undefined;
    }
    const topLevelId = SKILL_TAXONOMY_SELECTION_LIST_KEYS[0];
    if (!topLevelId) return undefined;
    return customSkillTree.slice(0, DEFAULT_SKILL_COUNT).map((node) => ({
      id: node.id,
      name: node.name,
      levelId: topLevelId,
    }));
  }, [needLive, customSkillTree]);

  useEffect(() => {
    if (!enabled) return;
    const key = `${result.source}:${currentSkillVersion ?? ''}`;
    if (notifiedRef.current === key) return;

    if (result.generatedStale) {
      notifiedRef.current = key;
      kibanaLogger?.error(
        `Skill default filters stale: generated ${GENERATED_SKILL_DEFAULTS_VERSION} != live ${currentSkillVersion}`
      );
      const id = 'skill-defaults-generated-stale';
      if (!toast.isActive(id)) {
        toast({
          id,
          title: 'Default skills could not be loaded',
          description:
            'A temporary skill selection is being shown. Please review and re-select your skills.',
          status: 'error',
          duration: 8000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } else if (result.savedStale) {
      notifiedRef.current = key;
      const id = 'skill-defaults-out-of-date';
      if (!toast.isActive(id)) {
        toast({
          id,
          title: 'Your default skills no longer apply',
          description:
            'They were chosen for a different skill taxonomy, so the standard defaults are showing. Set new defaults in Preferences.',
          status: 'info',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  }, [
    enabled,
    result.source,
    result.savedStale,
    result.generatedStale,
    currentSkillVersion,
    toast,
  ]);

  return { firstFromLiveSelections };
};
